/**
 * DFP Admin Service (PathfinderRF FE)
 * ===================================
 * Unified client for the DFP analysis page: watchlist CRUD, trusted-device
 * (exclusion) CRUD, baseline scan/diff/sessions, detector health/feedback, and
 * the operator -> TAK callsign mapping used by the proximity feature.
 *
 * Lives in the RF FE (not COP) so the dedicated DFP page has no cross-module
 * dependency. Mirrors the proven endpoints used by COP's forceProtectionService.
 */

// ---- Types ----------------------------------------------------------------

export interface WatchlistEntry {
  id: number;
  identifier?: string | null;
  normalized_identifier?: string | null;
  identifier_type: string;
  match_mode: string;
  pattern?: string | null;
  signal_type: string;
  geofence_id?: number | null;
  severity_floor: string;
  label?: string | null;
  reason?: string | null;
  enabled: boolean;
  latitude?: number | null;
  longitude?: number | null;
  first_seen?: string | null;
  last_seen?: string | null;
  networks?: string | null;
}

export interface TrustedDevice {
  id: number;
  identifier: string;
  normalized_identifier: string;
  identifier_type: string;
  label?: string | null;
  reason?: string | null;
  applies_to: string;
  enabled: boolean;
  geofence_id?: number | null;
  last_reviewed_at?: string | null;
  /** Provenance: 'manual' | 'auto_feedback' | 'auto_tofu'. */
  source?: string;
  /** Set for time-bounded auto-trust (auto_tofu); null = never expires. */
  expires_at?: string | null;
}

export interface BaselineDevice {
  signal_type: string;
  stable_identifier: string;
  normalized_identifier: string;
  identifier?: string | null;
  display_name?: string | null;
  manufacturer?: string | null;
  device_category?: string | null;
  is_la_bit: boolean;
  is_tracker: boolean;
  sighting_count: number;
  distinct_locations: number;
  max_rssi?: number | null;
  first_seen?: string | null;
  last_seen?: string | null;
  already_trusted: boolean;
  on_watchlist: boolean;
}

export interface BaselineScanResponse {
  geofence_id?: number | null;
  start?: string | null;
  end?: string | null;
  device_count: number;
  devices: BaselineDevice[];
}

export interface OperatorTakMapping {
  id: number;
  operator_device_id: string;
  tak_callsign: string;
  tak_uid?: string | null;
  label?: string | null;
  enabled: boolean;
  created_by?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface FeedbackSummaryRow {
  anomaly_type?: string;
  rule?: string;
  true_positive?: number;
  false_positive?: number;
  unclear?: number;
  total?: number;
  fp_rate?: number;
  [k: string]: any;
}

// ---- Service --------------------------------------------------------------

class DFPAdminService {
  private baseUrl = process.env.REACT_APP_API_URL || '/api';

  private headers() {
    return {
      Authorization: `Bearer ${localStorage.getItem('access_token') || ''}`,
      'Content-Type': 'application/json',
    };
  }

  private async req<T>(path: string, init?: RequestInit): Promise<T> {
    const resp = await fetch(`${this.baseUrl}${path}`, { headers: this.headers(), ...init });
    if (!resp.ok) {
      const text = await resp.text().catch(() => '');
      throw new Error(`HTTP ${resp.status}${text ? `: ${text}` : ''}`);
    }
    // DELETE handlers may return empty bodies
    const ct = resp.headers.get('content-type') || '';
    if (!ct.includes('application/json')) return undefined as unknown as T;
    return resp.json();
  }

  // ---- Watchlist (global + scoped) ----
  async listWatchlist(): Promise<WatchlistEntry[]> {
    return this.req<WatchlistEntry[]>('/admin/dfp/watchlist');
  }

  async createWatchlist(entry: Partial<WatchlistEntry>): Promise<WatchlistEntry> {
    return this.req<WatchlistEntry>('/admin/dfp/watchlist', {
      method: 'POST',
      body: JSON.stringify(entry),
    });
  }

  async deleteWatchlist(id: number): Promise<void> {
    await this.req(`/admin/dfp/watchlist/${id}`, { method: 'DELETE' });
  }

  // ---- Trusted devices (exclusions) ----
  async listTrusted(source?: string): Promise<TrustedDevice[]> {
    const qs = source ? `?source=${encodeURIComponent(source)}` : '';
    return this.req<TrustedDevice[]>(`/admin/dfp/exclusions${qs}`);
  }

  async createTrusted(params: {
    identifier: string;
    identifier_type?: string;
    label?: string;
    reason?: string;
    applies_to?: string;
    geofence_id?: number;
  }): Promise<TrustedDevice> {
    return this.req<TrustedDevice>('/admin/dfp/exclusions', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }

  async deleteTrusted(id: number): Promise<void> {
    await this.req(`/admin/dfp/exclusions/${id}`, { method: 'DELETE' });
  }

  // ---- Baseline ----
  async baselineScan(params: {
    geofence_id?: number;
    polygon?: any;
    start?: string;
    end?: string;
    signal_types?: string[];
    min_rssi?: number;
    min_sightings?: number;
  }): Promise<BaselineScanResponse> {
    return this.req<BaselineScanResponse>('/dfp/baseline/scan', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }

  // ---- Operator -> TAK mapping (proximity targeting) ----
  async listOperatorTak(): Promise<OperatorTakMapping[]> {
    return this.req<OperatorTakMapping[]>('/admin/dfp/operator-tak');
  }

  async upsertOperatorTak(entry: {
    operator_device_id: string;
    tak_callsign: string;
    tak_uid?: string;
    label?: string;
    enabled?: boolean;
  }): Promise<OperatorTakMapping> {
    return this.req<OperatorTakMapping>('/admin/dfp/operator-tak', {
      method: 'POST',
      body: JSON.stringify(entry),
    });
  }

  async deleteOperatorTak(id: number): Promise<void> {
    await this.req(`/admin/dfp/operator-tak/${id}`, { method: 'DELETE' });
  }

  // ---- Feedback / calibration ----
  async getFeedbackSummary(): Promise<{ summary: FeedbackSummaryRow[] } & Record<string, any>> {
    return this.req('/dfp/feedback/summary');
  }
}

export const dfpAdminService = new DFPAdminService();
